/**
 * Bubble lifetimes (spec 02 §4 Lifetime, Look, Capacity, §4.1), ported from rebb64 D_13BE - once per
 * main-loop pass, after the bubbles move - and the level-clear sweep of $16F7.
 */

import { burstBolts } from './bolts';
import { BLINK_TOGGLE, FULL, GONE, HIDDEN, POPPING, QUIET_POP, isTrappedCode } from './codes';
import { burstDrops } from './fire';
import { bySlot, foodFallOf, setCode, slotsUsed, syncView } from './slots';
import { systemOf } from './system';
import { BLINK_BELOW, CROWD_MIN_FREE, CROWD_POPS, SLOTS } from './tuning';
import type { Bubble, World } from '../types';

/**
 * L13C0: every slot's timer runs down each pass. At 0 a plain full bubble ($04) bursts quietly - no
 * points, no chain - and a trapped monster escapes; other types never time out. In the last 16 (on
 * the even counts below $11) a plain bubble blinks - $04 <-> $48, and while $48 it is left alone: it
 * does not float, burst, bunch or chain - and a trapped monster's sprite flashes. Then L1449.
 */
export function runTimers(world: World): void {
  for (const b of bySlot(world)) {
    const m = b.motion;
    m.timer = (m.timer - 1) & 0xff;
    if (m.timer === 0) {
      if (m.code === FULL) {
        m.saved = FULL;
        setCode(b, QUIET_POP);
      } else if (isTrappedCode(m.code)) {
        escape(world, b);
      }
    } else if (m.timer < BLINK_BELOW && (m.timer & 1) === 0) {
      if (m.code === FULL || m.code === HIDDEN) setCode(b, m.code ^ BLINK_TOGGLE);
      else if (isTrappedCode(m.code)) m.flashOff = !m.flashOff;
    }
    m.flash = isTrappedCode(m.code) && m.timer < BLINK_BELOW;
    syncView(b);
  }
  crowding(world);
}

/**
 * L13D2: the monster comes out where its sprite is (Y odd, X even), angry, into its escape fall (the
 * MONSTER stream moves it from 'escaping'); the bubble vanishes without a burst ($38).
 */
function escape(world: World, b: Bubble): void {
  const id = b.content.kind === 'monster' ? b.content.monsterId : -1;
  b.content = { kind: 'empty' };
  b.motion.flash = false;
  b.motion.flashOff = false;
  setCode(b, GONE);
  const mon = world.monsters.find((x) => x.id === id);
  if (!mon) return;
  mon.state = 'escaping';
  mon.angry = true;
  mon.bubbleId = -1;
  mon.x &= 0xfe;
  mon.y |= 1;
  world.events.push({ type: 'escaped', monsterId: mon.id });
}

/**
 * L1449: the C64 counts the slots whose type is $34 or more - free ones, bursting, vanishing, and the
 * blink's invisible half - and with fewer than 2 bursts the first two plain full bubbles from slot 17
 * down (new bubbles take the highest free slot, so these are the spec's "oldest" only roughly).
 */
function crowding(world: World): void {
  // Bubble food (type $4C) counts as free too.
  const food = world.items.filter((i) => foodFallOf(i) !== null).length;
  const free = SLOTS - slotsUsed(world) + food + world.bubbles.filter((b) => b.motion.code >= POPPING).length;
  if (free >= CROWD_MIN_FREE) return;
  let left = CROWD_POPS;
  for (const b of bySlot(world)) {
    if (left === 0) break;
    if (b.motion.code !== FULL) continue;
    b.motion.saved = FULL;
    setCode(b, QUIET_POP);
    left--;
  }
}

/**
 * $16F7..$171B: on the first pass with no monster left, every software sprite bursts quietly - the
 * bubbles, fire drops and lightning bolts; a red-cross shot ($44) flies on. (The bubbles that become
 * 700-point food, 02 §7.6, have already been taken by `levelClearBonus`, which the flow calls as the
 * level clears, before this pass.)
 */
export function clearSweep(world: World): void {
  const sys = systemOf(world);
  if (sys.cleared || world.flow.phase !== 'clear') return;
  sys.cleared = true;
  for (const b of world.bubbles) {
    b.motion.saved = b.motion.code;
    b.motion.burst = -1;
    setCode(b, QUIET_POP);
  }
  burstDrops(world);
  burstBolts(world);
}
